import { useEffect, useRef, type RefObject } from "react";

/**
 * Escape closes, and Tab stays inside.
 *
 * Without the trap, thirty focusable elements behind the overlay were still
 * reachable — so a keyboard user tabs out of the dialog into a page they
 * cannot see and has no way to know where they are.
 *
 * `onClose` is read through a ref rather than listed as a dependency. Parents
 * pass an inline arrow, so it is a new function on every parent render — and
 * the library re-renders every ten seconds while anything is processing. Each
 * of those re-ran this effect, whose cleanup puts focus back on the page
 * behind: focus left the input mid-word while someone was typing a link.
 */
export function useDialogKeys(
  dialogRef: RefObject<HTMLElement | null>,
  onClose: () => void,
): void {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onCloseRef.current();
        return;
      }
      if (e.key !== "Tab") return;

      const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable || focusable.length === 0) return;

      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      // Put focus back where it was, or it lands on <body> and the next Tab
      // starts from the top of the page.
      previouslyFocused?.focus?.();
    };
  }, [dialogRef]);
}
